import { BOARD } from "@grundbesitz/shared";

/**
 * Pure geometry for the board. Everything is expressed in SVG user units of a square
 * viewBox (0 0 SIZE SIZE). Field 0 (LOS) sits bottom right, the path runs clockwise:
 * bottom row to the right-to-left, up the left column, along the top, down the right column.
 * All text stays upright on every side; the color bar of a street always faces the board center.
 */

export const CORNER = 150;
export const CELL = 90;
export const SIZE = 2 * CORNER + 9 * CELL;
/** Thickness of the color bar of a street. */
export const BAR = 30;
/** Thickness of the owner stripe on the outer edge of an ownable field. */
export const STRIPE = 16;
/** Milliseconds a token needs per field. */
export const STEP_MS = 140;

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Point {
  x: number;
  y: number;
}

export type Side = "bottom" | "left" | "top" | "right" | "corner";

export interface FieldGeometry {
  index: number;
  side: Side;
  rect: Rect;
  /** Color bar of a street on the inner edge, null for every other field. */
  bar: Rect | null;
  /** Owner stripe on the outer edge, null for fields that cannot be owned. */
  stripe: Rect | null;
  /** Area left for name, symbol, price and tokens. */
  content: Rect;
  /** True for the horizontal (bottom and top) fields, which are taller than wide. */
  portrait: boolean;
}

export const INNER: Rect = { x: CORNER, y: CORNER, w: SIZE - 2 * CORNER, h: SIZE - 2 * CORNER };

function sideOf(index: number): Side {
  if (index % 10 === 0) return "corner";
  if (index < 10) return "bottom";
  if (index < 20) return "left";
  if (index < 30) return "top";
  return "right";
}

function rectOf(index: number): Rect {
  const side = sideOf(index);
  const far = SIZE - CORNER;
  switch (side) {
    case "corner":
      return {
        x: index === 0 || index === 30 ? far : 0,
        y: index === 0 || index === 10 ? far : 0,
        w: CORNER,
        h: CORNER,
      };
    case "bottom":
      return { x: far - index * CELL, y: far, w: CELL, h: CORNER };
    case "left":
      return { x: 0, y: far - (index - 10) * CELL, w: CORNER, h: CELL };
    case "top":
      return { x: CORNER + (index - 21) * CELL, y: 0, w: CELL, h: CORNER };
    case "right":
      return { x: far, y: CORNER + (index - 31) * CELL, w: CORNER, h: CELL };
  }
}

const geometryCache = new Map<number, FieldGeometry>();

export function fieldGeometry(index: number): FieldGeometry {
  const hit = geometryCache.get(index);
  if (hit) return hit;
  const side = sideOf(index);
  const rect = rectOf(index);
  const kind = BOARD[index]?.kind;
  const hasBar = kind === "STREET";
  const hasStripe = hasBar || kind === "RAILROAD" || kind === "UTILITY";
  const { x, y, w, h } = rect;
  const bar = hasBar ? BAR : 0;
  const stripe = hasStripe ? STRIPE : 0;

  let barRect: Rect | null = null;
  let stripeRect: Rect | null = null;
  let content: Rect = rect;
  switch (side) {
    case "bottom":
      barRect = hasBar ? { x, y, w, h: BAR } : null;
      stripeRect = hasStripe ? { x, y: y + h - STRIPE, w, h: STRIPE } : null;
      content = { x, y: y + bar, w, h: h - bar - stripe };
      break;
    case "top":
      barRect = hasBar ? { x, y: y + h - BAR, w, h: BAR } : null;
      stripeRect = hasStripe ? { x, y, w, h: STRIPE } : null;
      content = { x, y: y + stripe, w, h: h - bar - stripe };
      break;
    case "left":
      barRect = hasBar ? { x: x + w - BAR, y, w: BAR, h } : null;
      stripeRect = hasStripe ? { x, y, w: STRIPE, h } : null;
      content = { x: x + stripe, y, w: w - bar - stripe, h };
      break;
    case "right":
      barRect = hasBar ? { x, y, w: BAR, h } : null;
      stripeRect = hasStripe ? { x: x + w - STRIPE, y, w: STRIPE, h } : null;
      content = { x: x + bar, y, w: w - bar - stripe, h };
      break;
    case "corner":
      break;
  }
  const geometry: FieldGeometry = {
    index,
    side,
    rect,
    bar: barRect,
    stripe: stripeRect,
    content,
    portrait: side === "bottom" || side === "top",
  };
  geometryCache.set(index, geometry);
  return geometry;
}

export function rectCenter(r: Rect): Point {
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}

export function fieldCenter(index: number): Point {
  return rectCenter(fieldGeometry(index).rect);
}

// ---------------------------------------------------------------- token placement

export interface Slot extends Point {
  /** Edge length of the square a token may use. */
  size: number;
}

/** Places `count` tokens on a centered grid inside `area`. Tokens never overlap. */
export function tokenSlots(area: Rect, count: number, maxSize = 46): Slot[] {
  if (count <= 0) return [];
  const cols = count <= 2 ? count : count <= 4 ? 2 : 3;
  const rows = Math.ceil(count / cols);
  const size = Math.max(16, Math.floor(Math.min(maxSize, area.w / cols, area.h / rows)));
  const c = rectCenter(area);
  const slots: Slot[] = [];
  for (let i = 0; i < count; i++) {
    const row = Math.floor(i / cols);
    const inRow = row === rows - 1 ? count - row * cols : cols;
    const col = i - row * cols;
    slots.push({
      x: c.x - (inRow * size) / 2 + size / 2 + col * size,
      y: c.y - (rows * size) / 2 + size / 2 + row * size,
      size,
    });
  }
  return slots;
}

/** Where the tokens of a normal field stand: the free band between name and price for few tokens, the whole content for many. */
export function tokenArea(geometry: FieldGeometry, count: number): Rect {
  const c = geometry.content;
  if (geometry.side === "corner") return { x: c.x + 10, y: c.y + 10, w: c.w - 20, h: c.h - 20 };
  if (count >= 3) return { x: c.x + 2, y: c.y + 4, w: c.w - 4, h: c.h - 8 };
  const top = geometry.portrait ? 40 : 32;
  const bottom = geometry.portrait ? 20 : 16;
  return { x: c.x + 2, y: c.y + top, w: c.w - 4, h: c.h - top - bottom };
}

export interface JailLayout {
  /** The barred cell inside the corner. */
  cell: Rect;
  prisoners: Slot[];
  visitors: Slot[];
}

const JAIL_CELL = 96;
const VISITOR_SIZE = 30;

/** Corner 10: prisoners sit inside the cell behind bars, visitors along the outer edges. */
export function jailLayout(prisoners: number, visitors: number): JailLayout {
  const r = fieldGeometry(10).rect;
  const cell: Rect = { x: r.x + r.w - JAIL_CELL - 4, y: r.y + 4, w: JAIL_CELL, h: JAIL_CELL };
  const inner: Rect = { x: cell.x + 6, y: cell.y + 22, w: cell.w - 12, h: cell.h - 28 };
  const visitorSpots: Slot[] = [];
  for (let i = 0; i < 4; i++) visitorSpots.push({ x: r.x + 22 + i * 34, y: r.y + r.h - 22, size: VISITOR_SIZE });
  for (let i = 0; i < 3; i++) visitorSpots.push({ x: r.x + 24, y: r.y + 22 + i * 34, size: VISITOR_SIZE });
  const placed: Slot[] = [];
  for (let i = 0; i < visitors; i++) placed.push(visitorSpots[Math.min(i, visitorSpots.length - 1)]);
  return { cell, prisoners: tokenSlots(inner, prisoners, 34), visitors: placed };
}

// ---------------------------------------------------------------- movement

export interface MoveSteps {
  /** Fields a token visits one after another. */
  steps: number[];
  /** No sliding: the token jumps (jail). */
  instant: boolean;
}

export function movementSteps(move: { from: number; to: number; path: number[]; reason: "DICE" | "CARD" | "JAIL" }): MoveSteps {
  if (move.reason === "JAIL") return { steps: [move.to], instant: true };
  if (move.path.length === 0) return { steps: move.from === move.to ? [] : [move.to], instant: true };
  return { steps: move.path, instant: false };
}

/** Long trips (cards, wrap-arounds) speed up so they never take more than about two seconds. */
export function stepDelayMs(stepCount: number): number {
  if (stepCount <= 14) return STEP_MS;
  return Math.max(50, Math.floor(2000 / stepCount));
}

// ---------------------------------------------------------------- text

/** Word parts a long compound name may be broken in front of. */
const BREAK_BEFORE = ["straße", "platz", "allee", "bahnhof", "werk", "steuer", "feld"];

/** Splits a field name into at most two lines. Compounds break with a hyphen ("Chaussee-" / "straße"). */
export function splitName(name: string): string[] {
  const words = name.split(" ");
  if (words.length === 2) return words;
  if (words.length > 2) {
    const half = Math.ceil(words.length / 2);
    return [words.slice(0, half).join(" "), words.slice(half).join(" ")];
  }
  if (name.length <= 9) return [name];
  const lower = name.toLowerCase();
  let best = -1;
  for (const part of BREAK_BEFORE) {
    const at = lower.lastIndexOf(part);
    if (at >= 3 && at + part.length === lower.length && (best === -1 || Math.abs(at - name.length / 2) < Math.abs(best - name.length / 2))) {
      best = at;
    }
  }
  if (best === -1) return [name];
  const head = name.slice(0, best);
  // "Bahnhofstraße" -> "Bahnhofs-" keeps the joining s in front of the break.
  return [`${head}-`, name.slice(best)];
}

/** Rough text width in user units for the UI sans-serif at semibold weight. */
export function estimateTextWidth(text: string, fontSize: number): number {
  return text.length * fontSize * 0.575;
}

export interface FittedName {
  lines: string[];
  fontSize: number;
}

export function fitName(name: string, maxWidth: number, maxFont = 17, minFont = 11): FittedName {
  const lines = splitName(name);
  const longest = Math.max(...lines.map((l) => l.length));
  const fitting = Math.floor(maxWidth / (longest * 0.575));
  return { lines, fontSize: Math.max(minFont, Math.min(maxFont, fitting)) };
}

// ---------------------------------------------------------------- color

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: string): number {
  const h = hex.replace("#", "");
  const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export const INK_DARK = "#1b1a17";
export const INK_LIGHT = "#ffffff";

/** Text color with the better contrast on the given background. */
export function pickInk(background: string): string {
  return contrastRatio(background, INK_DARK) >= contrastRatio(background, INK_LIGHT) ? INK_DARK : INK_LIGHT;
}

// ---------------------------------------------------------------- placement of all tokens and buildings

export interface TokenEntry {
  id: string;
  /** Field the token is currently shown on. */
  field: number;
  /** In jail, only meaningful on field 10. */
  inJail: boolean;
}

export interface PlacedToken extends Slot {
  id: string;
  /** Stands behind the bars of the jail cell. */
  imprisoned: boolean;
}

/** Positions for every token. Tokens on the same field share it in input order; jail splits into prisoners and visitors. */
export function placeTokens(entries: TokenEntry[]): PlacedToken[] {
  const byField = new Map<number, TokenEntry[]>();
  for (const e of entries) {
    const list = byField.get(e.field);
    if (list) list.push(e);
    else byField.set(e.field, [e]);
  }
  const out: PlacedToken[] = [];
  for (const [field, group] of byField) {
    if (field === 10) {
      const prisoners = group.filter((e) => e.inJail);
      const visitors = group.filter((e) => !e.inJail);
      const layout = jailLayout(prisoners.length, visitors.length);
      prisoners.forEach((e, i) => out.push({ id: e.id, imprisoned: true, ...layout.prisoners[i] }));
      visitors.forEach((e, i) => out.push({ id: e.id, imprisoned: false, ...layout.visitors[i] }));
      continue;
    }
    const slots = tokenSlots(tokenArea(fieldGeometry(field), group.length), group.length, field % 10 === 0 ? 40 : 46);
    group.forEach((e, i) => out.push({ id: e.id, imprisoned: false, ...slots[i] }));
  }
  return out;
}

/** Centers for `count` building icons lined up along a color bar. */
export function buildingSlots(bar: Rect, count: number): Slot[] {
  if (count <= 0) return [];
  const horizontal = bar.w >= bar.h;
  const along = horizontal ? bar.w : bar.h;
  const across = horizontal ? bar.h : bar.w;
  const size = Math.min(across - 6, Math.floor((along - 6) / Math.max(count, 1)), 30);
  const c = rectCenter(bar);
  const slots: Slot[] = [];
  for (let i = 0; i < count; i++) {
    const offset = -((count * size) / 2) + size / 2 + i * size;
    slots.push(horizontal ? { x: c.x + offset, y: c.y, size } : { x: c.x, y: c.y + offset, size });
  }
  return slots;
}

/** Generic street endings that carry little information and are dropped on a small board. */
const GENERIC_ENDING = /^(straße|platz|allee)$/i;

/** Short form of a name for small boards: the generic ending is dropped and the rest is set larger. */
export function compactName(name: string, maxWidth: number, maxFont = 23, minFont = 12): FittedName {
  const lines = splitName(name);
  const kept = lines.length === 2 && GENERIC_ENDING.test(lines[1]) ? [lines[0].replace(/-$/, "")] : lines;
  const longest = Math.max(...kept.map((l) => l.length));
  const fitting = Math.floor(maxWidth / (longest * 0.575));
  return { lines: kept, fontSize: Math.max(minFont, Math.min(maxFont, fitting)) };
}
